import React from 'react'

/**
 * Renders markdown-flavoured output text as clean, polished HTML.
 * Strips raw markdown syntax (# - ---) and presents structured content.
 */

type LineType = 'h1' | 'h2' | 'h3' | 'bullet' | 'numbered' | 'empty' | 'paragraph' | 'divider'

interface ParsedLine {
  type: LineType
  content: string
}

function parseLine(line: string): ParsedLine {
  const trimmed = line.trim()
  if (
    /^---+$/.test(trimmed) ||
    /^___+$/.test(trimmed) ||
    /^\*\*\*+$/.test(trimmed) ||
    /^===+$/.test(trimmed) ||
    /^━+$/.test(trimmed) ||
    /^[━=\-_*]{3,}$/.test(trimmed) ||
    trimmed === '<hr>' ||
    trimmed === '<hr/>' ||
    trimmed === '<hr />'
  ) return { type: 'divider', content: '' }
  if (/^### /.test(line)) return { type: 'h3', content: line.replace(/^### /, '') }
  if (/^## /.test(line)) return { type: 'h2', content: line.replace(/^## /, '') }
  if (/^# /.test(line)) return { type: 'h1', content: line.replace(/^# /, '') }
  if (/^[-*•] /.test(trimmed)) return { type: 'bullet', content: trimmed.replace(/^[-*•] /, '') }
  if (/^\d+\. /.test(trimmed)) return { type: 'numbered', content: trimmed }
  if (trimmed === '') return { type: 'empty', content: '' }
  return { type: 'paragraph', content: line }
}

function renderInline(text: string): string {
  return text
    .replace(/<hr\s*\/?>/gi, '')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\s*—\s*/g, ' ')
    .replace(/\s*–\s*/g, ' ')
}

export const OutputRenderer = ({ content }: { content: string }) => {
  const lines = content.split('\n')
  const parsed = lines.map(parseLine)

  const elements: React.ReactNode[] = []
  let bulletBuffer: string[] = []
  let numberedBuffer: string[] = []
  let keyIdx = 0

  const flushBullets = () => {
    if (bulletBuffer.length) {
      elements.push(
        <ul key={`ul-${keyIdx++}`} className="space-y-1.5 my-3 pl-0">
          {bulletBuffer.map((b, i) => (
            <li key={i} className="flex items-start gap-2.5 text-[15px] text-gray-200 leading-relaxed">
              <span className="mt-[6px] w-1.5 h-1.5 rounded-full bg-[#FD802E] shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: renderInline(b) }} />
            </li>
          ))}
        </ul>
      )
      bulletBuffer = []
    }
  }

  const flushNumbered = () => {
    if (numberedBuffer.length) {
      elements.push(
        <ol key={`ol-${keyIdx++}`} className="space-y-1.5 my-3 pl-4 list-decimal list-outside">
          {numberedBuffer.map((b, i) => (
            <li key={i} className="text-[15px] text-gray-200 leading-relaxed pl-1"
              dangerouslySetInnerHTML={{ __html: renderInline(b.replace(/^\d+\. /, '')) }}
            />
          ))}
        </ol>
      )
      numberedBuffer = []
    }
  }

  for (const line of parsed) {
    if (line.type !== 'bullet') flushBullets()
    if (line.type !== 'numbered') flushNumbered()

    switch (line.type) {
      case 'divider':
        break

      case 'h1':
        elements.push(
          <h1 key={keyIdx++} className="text-xl font-bold text-white mt-8 mb-3 first:mt-0 pb-2 border-b border-[#2a4a5c]"
            dangerouslySetInnerHTML={{ __html: renderInline(line.content) }}
          />
        )
        break

      case 'h2':
        elements.push(
          <h2 key={keyIdx++} className="text-xs font-semibold text-[#FD802E] mt-6 mb-2 uppercase tracking-wide"
            dangerouslySetInnerHTML={{ __html: renderInline(line.content) }}
          />
        )
        break

      case 'h3':
        elements.push(
          <h3 key={keyIdx++} className="text-sm font-semibold text-white mt-4 mb-1.5"
            dangerouslySetInnerHTML={{ __html: renderInline(line.content) }}
          />
        )
        break

      case 'bullet':
        bulletBuffer.push(line.content)
        break

      case 'numbered':
        numberedBuffer.push(line.content)
        break

      case 'empty':
        elements.push(<div key={keyIdx++} className="h-2" />)
        break

      case 'paragraph':
        elements.push(
          <p key={keyIdx++} className="text-[15px] text-gray-200 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderInline(line.content) }}
          />
        )
        break
    }
  }

  flushBullets()
  flushNumbered()

  return <div className="space-y-0.5">{elements}</div>
}
